// Fahrpreis-Schätzung nach Unternehmens-Tarif (inkl. Nachttarif) für die
// Portal-Anzeige. Spiegelt AppSettings.isNachtTarif/effective* der Mobile-App
// und aiIsNachtTarif/getFareEstimate der Cloud Functions — bei Änderungen alle
// drei Schichten synchron halten. Anfahrtskosten kommen separat (siehe anfahrt.js).

const num = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseTimeToMinutes = (hhmm) => {
  const [h, m] = String(hhmm ?? '').split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
};

/**
 * Gilt zum gegebenen Zeitpunkt der Nachttarif? Für Vorbestellungen die geplante
 * Abholzeit übergeben, sonst „jetzt". Nutzt die lokale (deutsche) Browserzeit —
 * die CF rechnet explizit in Europe/Berlin, der Disponent sitzt in derselben Zone.
 */
export const isNachtTarif = (settings, dateTime = new Date()) => {
  if (!settings || settings.nachtTarifAktiv !== true) return false;
  const dt = dateTime instanceof Date ? dateTime : new Date(dateTime);
  if (Number.isNaN(dt.getTime())) return false;
  const minutes = dt.getHours() * 60 + dt.getMinutes();
  const isWeekend = dt.getDay() === 0 || dt.getDay() === 6; // So/Sa
  const startStr = isWeekend
    ? (settings.nachtTarifWochenendStart ?? '20:00')
    : (settings.nachtTarifWocheStart ?? '22:00');
  const endStr = isWeekend
    ? (settings.nachtTarifWochenendEnd ?? '07:00')
    : (settings.nachtTarifWocheEnd ?? '06:00');
  const start = parseTimeToMinutes(startStr);
  const end = parseTimeToMinutes(endStr);
  if (start <= end) {
    return minutes >= start && minutes < end;
  }
  // Fenster mit Mitternachts-Übergang (z. B. 22:00–06:00)
  return minutes >= start || minutes < end;
};

export const effectiveGrundpreis = (settings, dateTime) =>
  isNachtTarif(settings, dateTime)
    ? num(settings.nachtTarifGrundpreis, 3.5)
    : num(settings.tarifGrundpreis, 3.5);

export const effectivePreisProKm = (settings, dateTime) =>
  isNachtTarif(settings, dateTime)
    ? num(settings.nachtTarifPreisProKm, 2.5)
    : num(settings.tarifPreisProKm, 2.5);

/** Fähigkeiten, für die ein Zuschlag hinterlegt werden darf. */
export const SURCHARGE_CAPABILITIES = ['rollstuhl', 'tragestuhl', 'liegend'];

const toStringList = (value) => (Array.isArray(value) ? value.map(String) : []);

/**
 * Der greifende Spezialzuschlag, oder null (= es gilt die normale
 * Großraum-Regel). Siehe nextcab_mobile/docs/general/spezialzuschlaege_konzept.md §4.
 *
 * Mehrere angeforderte Fähigkeiten ergeben nie eine Summe, sondern den höchsten
 * Einzelbetrag — gefahren wird ein Fahrzeug, nicht zwei. Ein Ergebnis mit
 * `eur === 0` heißt: greift, steckt aber schon im Festpreis.
 *
 * @returns {{grund: string, eur: number}|null}
 */
export const spezialZuschlag = (
  settings,
  { capabilities, isKrankenfahrt = false, festpreisCapabilities } = {}
) => {
  // Verordnungsfahrten rechnen nach § 133 SGB V ab — kein Haustarif-Zuschlag.
  if (isKrankenfahrt) return null;
  if (!settings || settings.tarifSpezialZuschlaegeAktiv !== true) return null;
  const rates = settings.tarifSpezialZuschlaege;
  if (!rates || typeof rates !== 'object') return null;

  let grund = null;
  let betrag = 0;
  for (const cap of toStringList(capabilities)) {
    if (!SURCHARGE_CAPABILITIES.includes(cap)) continue;
    const eur = Number(rates[cap]);
    if (Number.isFinite(eur) && eur > betrag) {
      grund = cap;
      betrag = eur;
    }
  }
  if (!grund) return null;

  // Deckt der Festpreis die Fähigkeit ab, ist der Zuschlag einkalkuliert.
  const imFestpreis = toStringList(festpreisCapabilities).includes(grund);
  return { grund, eur: imFestpreis ? 0 : betrag };
};

/**
 * Fahrzeugbezogener Zuschlag einer Fahrt in €: Spezialzuschlag mit Vorrang,
 * sonst der Großraumzuschlag ab Fahrzeugtyp 2. Gestapelt wird nie.
 */
export const vehicleSurchargeEur = (
  settings,
  {
    vehicleType = 1,
    capabilities,
    isKrankenfahrt = false,
    festpreisCapabilities,
    // An Fahrzeugtypen gebundener Festpreis: der Betrag ist der fertige Preis
    // für dieses Fahrzeug, der Großraumzuschlag entfällt
    // (nextcab_mobile/docs/general/festpreis_varianten_konzept.md §4).
    festpreisCoversVehicleType = false,
  } = {}
) => {
  const spezial = spezialZuschlag(settings, {
    capabilities,
    isKrankenfahrt,
    festpreisCapabilities,
  });
  if (spezial) return spezial.eur;
  if (festpreisCoversVehicleType) return 0;
  return Number(vehicleType) >= 2 ? num(settings?.tarifGrossraumzuschlag, 5.0) : 0;
};

/**
 * Passt ein Festpreis-Eintrag zu Fahrzeugtyp und angeforderten Fähigkeiten?
 * Leere Bindungsfelder passen immer; Fahrzeugtyp 0 (= keiner gewählt) schließt
 * jeden typgebundenen Eintrag aus (Konzept §3).
 */
export const festpreisMatchesRequest = (
  festpreis,
  { vehicleType, capabilities } = {}
) => {
  const types = Array.isArray(festpreis?.vehicleTypes)
    ? festpreis.vehicleTypes.map(Number).filter(Number.isFinite)
    : [];
  if (types.length > 0) {
    const vt = Number(vehicleType) || 0;
    if (!vt || !types.includes(vt)) return false;
  }
  const required = toStringList(festpreis?.specialCapabilities);
  if (required.length > 0) {
    const requested = new Set(toStringList(capabilities));
    if (!required.every((cap) => requested.has(cap))) return false;
  }
  return true;
};

/** Spezifität eines Festpreis-Eintrags — höher gewinnt (Konzept §3.1). */
export const festpreisSpecificity = (festpreis) =>
  (toStringList(festpreis?.specialCapabilities).length > 0 ? 2 : 0) +
  (Array.isArray(festpreis?.vehicleTypes) && festpreis.vehicleTypes.length > 0 ? 1 : 0);

/** Beschriftung eines Spezialzuschlags für die Anzeige. */
export const spezialZuschlagLabel = (grund) => {
  switch (grund) {
    case 'rollstuhl':
      return 'Rollstuhlgebühr';
    case 'tragestuhl':
      return 'Tragestuhlgebühr';
    case 'liegend':
      return 'Liegendgebühr';
    default:
      return 'Zuschlag';
  }
};

/**
 * Geschätzter Fahrpreis (ohne Anfahrt) nach Taxameter-Tarif, auf volle 10 Cent
 * gerundet — wie _calculateEstimatedEarningEur (App) und getFareEstimate (CF).
 * Der fahrzeugbezogene Zuschlag kommt aus vehicleSurchargeEur: Spezialzuschlag
 * mit Vorrang, sonst Großraum/Kleinbus.
 *
 * @returns {number|null} null, wenn keine Distanz vorliegt.
 */
export const calculateEstimatedFareEur = (
  settings,
  {
    distanceKm,
    vehicleType = 1,
    dateTime = new Date(),
    capabilities,
    isKrankenfahrt = false,
  }
) => {
  if (!settings || !Number.isFinite(distanceKm)) return null;
  const fare =
    effectiveGrundpreis(settings, dateTime) +
    distanceKm * effectivePreisProKm(settings, dateTime) +
    vehicleSurchargeEur(settings, { vehicleType, capabilities, isKrankenfahrt });
  return Math.round(fare * 10) / 10;
};
